import React, { useEffect, useState } from 'react';
import AdminCompaniesPage from './AdminCompaniesPage';
import AdminDevicesPage from './AdminDevicesPage';
import AdminDeviceDetailPage from './AdminDeviceDetailPage';
import AdminLogsPage from './AdminLogsPage';
import AdminTariffsPage from './AdminTariffsPage';
import AdminPortalSettingsPage from './AdminPortalSettingsPage';
import AdminLoginPage from './AdminLoginPage';

type AdminSection = 'companies' | 'devices' | 'logs' | 'tariffs' | 'portal-settings';

const SB: React.CSSProperties = {
  width: 200,
  flexShrink: 0,
  background: 'var(--sidebar-bg)',
  borderRight: '1px solid var(--glass-border)',
  display: 'flex',
  flexDirection: 'column',
};

const AdminPage: React.FC = () => {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem('admin_token'));
  const [section, setSection] = useState<AdminSection>('companies');
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  useEffect(() => {
    const onLogout = () => setAuthed(false);
    window.addEventListener('admin-logout', onLogout);
    return () => window.removeEventListener('admin-logout', onLogout);
  }, []);

  if (!authed) {
    return <AdminLoginPage onSuccess={() => setAuthed(true)} />;
  }

  const nav = (s: AdminSection) => () => { setSection(s); setSelectedDeviceId(null); };

  return (
    <div style={{ display: 'flex', height: 'var(--app-h)', overflow: 'hidden', background: 'var(--body-gradient)', fontFamily: '"DM Sans", system-ui, sans-serif', fontSize: 14, color: 'var(--text-primary)' }}>

      {/* Sidebar */}
      <div style={SB}>
        {/* Logo */}
        <div style={{ padding: '14px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, var(--accent), var(--accent-light))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, boxShadow: '0 4px 12px var(--accent-glow)', flexShrink: 0 }}>▶</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>PromoVideo</div>
            <div style={{ fontSize: 10, color: 'var(--text-disabled)' }}>Admin Panel</div>
          </div>
        </div>

        {/* Nav */}
        <div style={{ flex: 1, padding: '8px', overflowY: 'auto' }}>
          <SbSect label="Administration" />
          <SbItem icon="🏢" label="Companies" active={section === 'companies'} onClick={nav('companies')} />
          <SbItem icon="🖥" label="Devices" active={section === 'devices'} onClick={nav('devices')} />

          <SbSect label="System" />
          <SbItem icon="📋" label="Logs" active={section === 'logs'} onClick={nav('logs')} />
          <SbItem icon="⚙️" label="Portal Settings" active={section === 'portal-settings'} onClick={nav('portal-settings')} />

          <SbSect label="Billing" />
          <SbItem icon="💳" label="Tariffs" active={section === 'tariffs'} onClick={nav('tariffs')} />
        </div>

        {/* Footer */}
        <div style={{ padding: '12px', borderTop: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-disabled)' }}>Admin panel</div>
          <div
            onClick={() => {
              localStorage.removeItem('admin_token');
              window.dispatchEvent(new Event('admin-logout'));
            }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8, cursor: 'pointer', color: '#f87171', fontSize: 13, fontWeight: 500, transition: 'background .12s' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,113,113,.1)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <span style={{ fontSize: 14 }}>↪</span> Logout
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {section === 'companies'       && <AdminCompaniesPage />}
        {section === 'devices' && !selectedDeviceId && <AdminDevicesPage onDeviceClick={setSelectedDeviceId} />}
        {section === 'devices' && selectedDeviceId && <AdminDeviceDetailPage deviceId={selectedDeviceId} onBack={() => setSelectedDeviceId(null)} />}
        {section === 'logs'            && <AdminLogsPage />}
        {section === 'tariffs'         && <AdminTariffsPage />}
        {section === 'portal-settings' && <AdminPortalSettingsPage />}
      </div>

    </div>
  );
};

const SbSect: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.12em', color: 'var(--text-disabled)', padding: '12px 8px 5px' }}>{label}</div>
);

interface SbItemProps { icon: string; label: string; active: boolean; onClick: () => void; badge?: number; }
const SbItem: React.FC<SbItemProps> = ({ icon, label, active, onClick, badge }) => (
  <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, marginBottom: 2, cursor: 'pointer', borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`, background: active ? 'var(--selected-bg)' : 'transparent', transition: 'all .12s' }}>
    <span style={{ fontSize: 14 }}>{icon}</span>
    <span style={{ fontSize: 13, color: active ? 'var(--accent-light)' : 'var(--text-secondary)', fontWeight: active ? 600 : 400 }}>{label}</span>
    {badge !== undefined && (
      <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, background: 'var(--accent-glow)', color: 'var(--accent-light)', padding: '1px 6px', borderRadius: 10 }}>{badge}</span>
    )}
  </div>
);

export default AdminPage;
